import * as React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { DayPickerSingleProps } from 'react-day-picker';
import { CalendarRange as CalendarIcon } from 'lucide-react';
import { format as dateFormat, parse as dateParse, isValid as isValidDate } from 'date-fns';

import { cn } from '@epoch-ui/lib/utils';
import { generateTimeOptions } from '@epoch-ui/lib/time.utils';
import { Button } from '@epoch-ui/components/Button';
import { Input } from '@epoch-ui/components/Input';
import { Calendar } from '@epoch-ui/components/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@epoch-ui/components/Popover/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@epoch-ui/components/Select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  CompositeFormMessage
} from '@epoch-ui/components/Form/form';

const DEFAULT_HOUR = 10;

interface DateTimePickerFieldContextValue {
  id: string;
  name: string;
  disabled?: boolean;
  allDay?: boolean;
}

const DateTimePickerFieldContext = React.createContext<DateTimePickerFieldContextValue | null>(
  null
);

const useDateTimePickerFieldContext = (): DateTimePickerFieldContextValue => {
  const context = React.useContext(DateTimePickerFieldContext);
  if (!context) {
    throw new Error(
      'DateTimePickerField compound components must be used within DateTimePickerField'
    );
  }
  return context;
};

export interface DateTimePickerFieldProps {
  /** Form field name for React Hook Form */
  name: string;
  /** Field label text or element */
  label?: React.ReactNode;
  /** Helper text displayed below label */
  description?: React.ReactNode;
  /** CSS classes for root container */
  className?: string;
  /** Disable entire field */
  disabled?: boolean;
  /** Hide time picker for all-day events */
  allDay?: boolean;
  /** Sub-components (Date and/or Time) */
  children: React.ReactNode;
}

interface DateInputProps extends Omit<DayPickerSingleProps, 'mode' | 'selected' | 'onSelect'> {
  /** Placeholder text when no date selected */
  placeholder?: string;
  /** CSS classes for date button */
  className?: string;
  /** Popover alignment relative to trigger */
  popoverAlign?: 'start' | 'center' | 'end';
}

interface TypeableDateInputProps
  extends Omit<DayPickerSingleProps, 'mode' | 'selected' | 'onSelect'> {
  /** Placeholder text when no date selected */
  placeholder?: string;
  /** CSS classes for the input container */
  className?: string;
  /** Popover alignment relative to trigger */
  popoverAlign?: 'start' | 'center' | 'end';
  /** Display format for the date (default: 'MMM dd, yyyy') */
  displayFormat?: string;
  /** Formats to attempt when parsing typed input (default: common formats) */
  parseFormats?: string[];
}

interface TimeInputProps {
  /** Placeholder text when no time selected */
  placeholder?: string;
  /** CSS classes for time select */
  className?: string;
  /** Time interval in minutes for dropdown options */
  interval?: 5 | 15 | 30 | 60;
}

/**
 * DateTimePickerField.Date - Date selection sub-component
 *
 * @description
 * Renders a date picker button that opens a calendar popover.
 * Accepts all react-day-picker props for calendar customization.
 *
 * @example
 * ```tsx
 * <DateTimePickerField.Date
 *   placeholder="Select date"
 *   popoverAlign="start"
 *   fromYear={2024}
 *   disabled={(date) => date < new Date()}
 * />
 * ```
 */
const DateInputComponent = React.memo<DateInputProps>(
  ({ placeholder = 'Pick a date', className, popoverAlign = 'start', ...calendarProps }) => {
    const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);

    const { name, id: fieldId, disabled } = useDateTimePickerFieldContext();
    const {
      field,
      fieldState: { error }
    } = useController({ name });

    const selectedDateTime = field.value instanceof Date ? field.value : null;

    const hasError = !!error?.message || Object.keys(error || {}).includes('date');

    const calendarDisabled = field.disabled ? true : calendarProps?.disabled;

    const handleDateChange = (date: Date | undefined): void => {
      if (date) {
        const newDateTime = new Date(date);
        if (selectedDateTime) {
          newDateTime.setHours(selectedDateTime.getHours());
          newDateTime.setMinutes(selectedDateTime.getMinutes());
          newDateTime.setSeconds(selectedDateTime.getSeconds());
        } else {
          newDateTime.setHours(DEFAULT_HOUR, 0, 0, 0);
        }
        field.onChange(newDateTime);
      }
      setIsCalendarOpen(false);
    };

    const dateButtonId = `${fieldId}-date`;
    const calendarId = `${fieldId}-calendar`;

    return (
      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <PopoverTrigger asChild>
          <FormControl>
            <Button
              id={dateButtonId}
              name={`${name}.date`}
              variant="outline"
              className={cn(
                'tw-m-0 tw-grow tw-basis-0 tw-justify-between tw-rounded-lg tw-font-normal disabled:tw-cursor-not-allowed',
                !selectedDateTime && 'tw-text-muted-foreground',
                hasError &&
                  'tw-border-destructive-foreground tw-ring-destructive-foreground focus:tw-border-destructive-foreground',
                className
              )}
              onClick={() => setIsCalendarOpen(true)}
              disabled={disabled}
              type="button"
              aria-haspopup="dialog"
              aria-expanded={isCalendarOpen}
              aria-controls={calendarId}
              aria-describedby={error ? `${fieldId}-error` : undefined}
            >
              {selectedDateTime ? dateFormat(selectedDateTime, 'M/d/yyyy') : placeholder}
              <CalendarIcon
                size={20}
                className="tw-ml-3 tw-text-muted-foreground"
                aria-hidden="true"
              />
            </Button>
          </FormControl>
        </PopoverTrigger>
        <PopoverContent
          className="tw-w-auto tw-p-0"
          align={popoverAlign}
          id={calendarId}
          role="dialog"
          aria-label="Date picker"
        >
          <Calendar
            mode="single"
            selected={selectedDateTime || undefined}
            onSelect={handleDateChange}
            disabled={calendarDisabled}
            initialFocus
            defaultMonth={selectedDateTime || undefined} // Open popover to the month of selected/default date
            {...calendarProps}
          />
        </PopoverContent>
      </Popover>
    );
  }
);

DateInputComponent.displayName = 'DateTimePickerField.Date';

const DEFAULT_DISPLAY_FORMAT = 'M/d/yyyy';
const DEFAULT_PARSE_FORMATS = [
  'MMM dd, yyyy',
  'MMMM dd, yyyy',
  'MM/dd/yyyy',
  'M/d/yyyy',
  'yyyy-MM-dd',
  'MM-dd-yyyy'
];

/**
 * DateTimePickerField.DateInput - Typeable date input sub-component
 *
 * @description
 * Renders a text input where users can type a date, with a calendar popover button.
 * Accepts all react-day-picker props for calendar customization.
 *
 * @example
 * ```tsx
 * <DateTimePickerField.DateInput
 *   placeholder="Enter date"
 *   popoverAlign="end"
 *   displayFormat="MMMM dd, yyyy"
 * />
 * ```
 */
const TypeableDateInputComponent = React.memo<TypeableDateInputProps>(
  ({
    placeholder = 'Select date',
    className,
    popoverAlign = 'end',
    displayFormat = DEFAULT_DISPLAY_FORMAT,
    parseFormats = DEFAULT_PARSE_FORMATS,
    ...calendarProps
  }) => {
    const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);
    const [draftValue, setDraftValue] = React.useState('');
    const [calendarMonth, setCalendarMonth] = React.useState<Date | undefined>(undefined);

    const { name, id: fieldId, disabled } = useDateTimePickerFieldContext();
    const {
      field,
      fieldState: { error }
    } = useController({ name });
    const { setError, clearErrors } = useFormContext();

    const selectedDateTime = field.value instanceof Date ? field.value : null;
    const hasError = !!error?.message || Object.keys(error || {}).includes('date');
    const calendarDisabled = field.disabled ? true : calendarProps?.disabled;

    const getDisplayValue = (): string => {
      if (isEditing) return draftValue;
      if (selectedDateTime) return dateFormat(selectedDateTime, displayFormat);
      return '';
    };
    const inputValue = getDisplayValue();

    const tryParseDate = React.useCallback(
      (value: string): Date | null => {
        const trimmed = value.trim();
        // Require at least 6 chars to avoid parsing partial input like "1" or "01/"
        if (trimmed.length < 6) return null;

        const match = parseFormats.reduce<Date | null>((found, fmt) => {
          if (found) return found;
          try {
            const parsed = dateParse(trimmed, fmt, new Date());
            if (
              isValidDate(parsed) &&
              parsed.getFullYear() >= 1900 &&
              parsed.getFullYear() <= 2100
            ) {
              const roundTrip = dateFormat(parsed, fmt);
              if (roundTrip === trimmed) {
                return parsed;
              }
            }
          } catch {
            // Continue to next format
          }
          return null;
        }, null);

        return match;
      },
      [parseFormats]
    );

    const updateFieldValue = (newDate: Date): void => {
      const newDateTime = new Date(newDate);
      if (selectedDateTime) {
        newDateTime.setHours(selectedDateTime.getHours());
        newDateTime.setMinutes(selectedDateTime.getMinutes());
        newDateTime.setSeconds(selectedDateTime.getSeconds());
      } else {
        newDateTime.setHours(DEFAULT_HOUR, 0, 0, 0);
      }
      field.onChange(newDateTime);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = e.target;
      setDraftValue(value);

      if (value.trim() === '') {
        field.onChange(undefined);
        setCalendarMonth(undefined);
        clearErrors(`${name}.date`);
        return;
      }

      const parsedDate = tryParseDate(value);
      if (parsedDate) {
        updateFieldValue(parsedDate);
        setCalendarMonth(parsedDate);
        clearErrors(`${name}.date`);
      } else {
        setError(`${name}.date`, {
          type: 'manual',
          message: 'Please enter date in MM/DD/YYYY format'
        });
      }
    };

    const handleInputFocus = (): void => {
      setIsEditing(true);
      setDraftValue(selectedDateTime ? dateFormat(selectedDateTime, displayFormat) : '');
    };

    const handleInputBlur = (): void => {
      setIsEditing(false);
      field.onBlur();
      if (draftValue.trim() === '') {
        clearErrors(`${name}.date`);
      } else if (!tryParseDate(draftValue)) {
        setError(`${name}.date`, {
          type: 'manual',
          message: 'Please enter date in MM/DD/YYYY format'
        });
      } else {
        clearErrors(`${name}.date`);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIsCalendarOpen(true);
      }
    };

    const handleDateSelect = (date: Date | undefined): void => {
      if (date) {
        updateFieldValue(date);
        clearErrors(`${name}.date`);
      }
      setIsCalendarOpen(false);
    };

    const dateInputId = `${fieldId}-date`;
    const calendarId = `${fieldId}-calendar`;

    return (
      <FormControl className="tw-mt-0">
        <div
          className={cn(
            'tw-flex tw-h-12 tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-border-grey',
            hasError && 'tw-border-destructive-foreground',
            disabled && 'tw-cursor-not-allowed tw-opacity-50',
            className
          )}
        >
          <Input
            id={dateInputId}
            name={`${name}.date`}
            value={inputValue}
            placeholder={placeholder}
            disabled={disabled}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            className="tw-h-full tw-max-w-[110px] tw-border-0 tw-bg-transparent focus-visible:tw-border-0 focus-visible:tw-ring-0 "
            aria-describedby={error ? `${fieldId}-error` : undefined}
          />
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="tw-m-0 tw-h-10 tw-w-10 tw-shrink-0 tw-p-0 "
                disabled={disabled}
                aria-label="typeable date input calendar"
                aria-haspopup="dialog"
                aria-expanded={isCalendarOpen}
                aria-controls={calendarId}
              >
                <CalendarIcon size={20} className="tw-text-muted-foreground" aria-hidden="true" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="tw-w-auto tw-p-0"
              align={popoverAlign}
              alignOffset={-8}
              sideOffset={10}
              id={calendarId}
              role="dialog"
              aria-label="Date picker"
            >
              <Calendar
                mode="single"
                selected={selectedDateTime || undefined}
                month={calendarMonth ?? selectedDateTime ?? undefined}
                onMonthChange={setCalendarMonth}
                onSelect={handleDateSelect}
                disabled={calendarDisabled}
                initialFocus
                {...calendarProps}
              />
            </PopoverContent>
          </Popover>
        </div>
      </FormControl>
    );
  }
);

TypeableDateInputComponent.displayName = 'DateTimePickerField.DateInput';

/**
 * DateTimePickerField.Time - Time selection sub-component
 *
 * @description
 * Renders a select dropdown for time selection.
 * Automatically hidden when parent has allDay=true.
 *
 * @example
 * ```tsx
 * <DateTimePickerField.Time
 *   placeholder="Select time"
 *   interval={15}
 *   className="tw-min-w-[120px]"
 * />
 * ```
 */
const TimeInputComponent = React.memo<TimeInputProps>(
  ({ placeholder = 'Pick a time', className, interval = 15 }) => {
    const { name, id: fieldId, allDay, disabled } = useDateTimePickerFieldContext();
    const {
      field,
      fieldState: { error }
    } = useController({ name });

    const hasError = !!error?.message || Object.keys(error || {}).includes('time');

    const selectedDateTime = field.value instanceof Date ? field.value : null;
    const selectedTimeValue = selectedDateTime ? dateFormat(selectedDateTime, 'HH:mm') : '';

    const handleTimeChange = (timeValue: string): void => {
      if (timeValue) {
        const [hours, minutes] = timeValue.split(':').map(Number);
        if (selectedDateTime) {
          // Create NEW date with new time
          const newDateTime = new Date(selectedDateTime);
          newDateTime.setHours(hours);
          newDateTime.setMinutes(minutes);
          field.onChange(newDateTime);
        } else {
          // Create date with today + selected time
          const newDateTime = new Date();
          newDateTime.setHours(hours);
          newDateTime.setMinutes(minutes);
          newDateTime.setSeconds(0);
          newDateTime.setMilliseconds(0);
          field.onChange(newDateTime);
        }
      }
    };

    const intervalTimeOptions = React.useMemo(() => generateTimeOptions(interval), [interval]);
    const timeOptions = React.useMemo(() => {
      // If the selected time is not in the interval time options, add it to the beginning
      if (
        selectedTimeValue &&
        selectedTimeValue !== '' &&
        intervalTimeOptions.findIndex((option) => option.value === selectedTimeValue) === -1
      ) {
        return [
          { value: selectedTimeValue, label: dateFormat(selectedDateTime!, 'hh:mm a') },
          ...intervalTimeOptions
        ].sort((a, b) => a.value.localeCompare(b.value));
      }

      return intervalTimeOptions;
    }, [intervalTimeOptions, selectedTimeValue]);

    if (allDay) {
      return null;
    }

    return (
      <FormControl>
        <Select
          name={`${name}.time`}
          value={selectedTimeValue}
          onValueChange={handleTimeChange}
          disabled={disabled || !selectedDateTime}
        >
          <SelectTrigger
            id={`${fieldId}-time`}
            className={cn(
              'tw-h-12 tw-grow tw-basis-0 tw-justify-start tw-border tw-text-left tw-font-normal',
              !selectedDateTime && 'tw-text-muted-foreground',
              hasError &&
                'tw-border-destructive-foreground tw-ring-destructive-foreground focus:tw-border-destructive-foreground',
              className
            )}
            aria-describedby={error ? `${fieldId}-error` : undefined}
            aria-label="Select time"
          >
            <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
              <SelectValue placeholder={placeholder} />
            </div>
          </SelectTrigger>
          <SelectContent>
            {timeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormControl>
    );
  }
);

TimeInputComponent.displayName = 'DateTimePickerField.Time';

// Root component
const DateTimePickerFieldRoot = React.forwardRef<HTMLDivElement, DateTimePickerFieldProps>(
  ({ name, label, description, className, disabled, allDay, children }, ref) => {
    const { control } = useFormContext();
    const fieldId = React.useId();

    const contextValue: DateTimePickerFieldContextValue = React.useMemo(
      () => ({
        id: fieldId,
        name,
        allDay,
        disabled
      }),
      [name, fieldId, allDay]
    );
    return (
      <FormField
        control={control}
        name={name}
        render={() => {
          return (
            <DateTimePickerFieldContext.Provider value={contextValue}>
              <FormItem className={className} ref={ref}>
                {label && (
                  <FormLabel
                    className={cn('tw-mb-2', description && 'tw-mb-1')}
                    htmlFor={`${fieldId}-date`}
                  >
                    {label}
                  </FormLabel>
                )}
                {description && (
                  <FormDescription className="tw-mb-2">{description}</FormDescription>
                )}
                <div
                  className={cn('tw-flex tw-w-full tw-items-center tw-gap-x-2')}
                  role="group"
                  aria-labelledby={label ? `${fieldId}-label` : undefined}
                >
                  {children}
                </div>
                <CompositeFormMessage id={`${fieldId}-error`} />
              </FormItem>
            </DateTimePickerFieldContext.Provider>
          );
        }}
      />
    );
  }
);

DateTimePickerFieldRoot.displayName = 'DateTimePickerField';

/**
 * DateTimePickerField - A compound form field component for selecting date and time
 *
 * @description
 * A flexible, accessible date/time picker built with compound component pattern.
 * Integrates with React Hook Form and supports nested validation errors.
 *
 * @example
 * Basic usage:
 * ```tsx
 * <DateTimePickerField name="eventDate" label="Event Date">
 *   <DateTimePickerField.Date />
 *   <DateTimePickerField.Time />
 * </DateTimePickerField>
 * ```
 *
 * @example
 * With typeable date input (allows user to type dates):
 * ```tsx
 * <DateTimePickerField name="eventDate" label="Event Date">
 *   <DateTimePickerField.DateInput placeholder="Enter or select date" />
 *   <DateTimePickerField.Time />
 * </DateTimePickerField>
 * ```
 *
 * @example
 * All-day event (time picker hidden):
 * ```tsx
 * <DateTimePickerField name="eventDate" label="Event Date" allDay>
 *   <DateTimePickerField.Date />
 *   <DateTimePickerField.Time />
 * </DateTimePickerField>
 * ```
 *
 * @example
 * With custom styling and calendar props:
 * ```tsx
 * <DateTimePickerField name="eventDate" label="Event Date" description="When does it start?">
 *   <DateTimePickerField.Date
 *     placeholder="Choose date"
 *     popoverAlign="center"
 *     fromYear={2024}
 *     toYear={2025}
 *   />
 *   <DateTimePickerField.Time
 *     interval={30}
 *     placeholder="Choose time"
 *   />
 * </DateTimePickerField>
 * ```
 *
 * @example
 * Nested validation with Zod:
 * ```tsx
 * // Schema supports nested errors for date/time individually
 * const schema = z.object({
 *   eventDate: z.object({
 *     date: z.date().min(new Date(), "Date must be in future"),
 *     time: z.string().min(1, "Time is required")
 *   }).or(z.date())
 * });
 * ```
 *
 * @features
 * - 🔧 Compound component pattern - compose only what you need
 * - 📅 React Hook Form integration with nested error support
 * - ⌨️ Typeable date input with auto-parsing (DateInput sub-component)
 * - ♿ Full accessibility with ARIA labels and keyboard navigation
 * - 🎨 Highly customizable styling and behavior
 * - 🕐 Configurable time intervals (5, 15, 30, 60 minutes)
 *
 * @requires React Hook Form context via useFormContext()
 */
const DateTimePickerField = Object.assign(DateTimePickerFieldRoot, {
  Date: DateInputComponent,
  DateInput: TypeableDateInputComponent,
  Time: TimeInputComponent
});

export { DateTimePickerField };
